/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output } from '@angular/core';
import { LbRbacService } from '../../../modules/loadbalancer/shared/services/utilities';
import { Router } from '@angular/router';
import { TabComponent } from './tab.component';
import { TabService } from '../../services';
import { environment, ModulesEnum } from '../../../../environments/environment';

export class TabModel {
    active: boolean;
    disabled: boolean;
    htmlId: string;
    iconAlignment: string;
    iconLocation: string;
    iconStyle: string;
    id: string;
    tabTitle: string;
}

@Component({
    selector: 'app-tab-container',
    templateUrl: './tab.container.component.html',
    styleUrls: ['./tab.component.scss']
})

export class TabContainerComponent implements AfterContentInit {
    /**
     * Store tab list.
     */
    @ContentChildren(TabComponent) public componentTabs: any;

    /**
     * Store active tab id.
     */
    @Input() public activeTabId: string;

    /**
     * Tab id
     */
    @Input() public id: string;

    /**
     * Add the vertical class
     */
    @Input() public vertical: boolean;

    /**
     * Tab container
     */
    @Input() public contained: boolean = false;

    /**
     * Tab width
     */
    @Input() public width: number;

    /**
     * Tab width percentage
     */
    @Input() public widthPercent: number;

    /**
     * Tab min height
     */
    @Input() public minHeight: number;

    /**
     * Disable border
     */
    @Input() public disableBorder: boolean;

    /**
     * Disable border
     */
    @Input() public tabsAlignment: 'left' | 'center' | 'right' = 'left';

    /**
     * Set the permissions for each of the tabs.
     * @param tabPermissions
     */
    @Input() public tabPermissions: any;

    /**
     * Emit changes
     */
    @Output() public eventEmitter: EventEmitter<any> = new EventEmitter();

    /**
     * Tabs for the view.
     */
    public tabs: TabModel[] = [];

    constructor(
        private lbRbacService: LbRbacService,
        private router: Router,
        private tabService: TabService
    ) {
    }

    /**
     * Get the Modules ENUM, so we can use it in the view.
     */
    get modulesEnum(): any {
        return ModulesEnum;
    }

    public ngAfterContentInit(): void {
        this.setUpContainer();
    }

    /**
     * Setup container.
     * @private
     */
    private setUpContainer(): void {
        const allTabs: any = this.componentTabs.toArray();
        this.setTabIds(allTabs);
        const availableTabs: any[] = this.getAvailableTabs(allTabs);

        if (!availableTabs.length) {
            this.redirectToAccessDenied();
        } else {
            this.setUpComponent(availableTabs, allTabs);
        }
    }

    /**
     * Set tab ids in DOM.
     * @param tabs
     * @private
     */
    private setTabIds(tabs: any[]): void {
        for (const tab of tabs) {
            tab.htmlId = tab.tabTitle.split(' ').join('-').toLowerCase();
        }
    }

    /**
     * Redirect to access denied.
     * @private
     */
    private redirectToAccessDenied(): void {
        const baseUrl: string = this.router.url;
        const url: string = environment.module === this.modulesEnum.LOADBALANCER
            ? this.lbRbacService.stripApplianceIdFromUrl(baseUrl)
            : baseUrl;
        this.router.navigateByUrl(baseUrl + '/no-access', { state: { url: url.toString() } }).then().catch();
    }

    /**
     * Set up component.
     * @param availableTabs
     * @param allTabs
     * @private
     */
    private setUpComponent(availableTabs: any[], allTabs: TabModel[]): void {
        const activeTabId: string = this.tabService.activeTabId ? this.tabService.activeTabId : this.activeTabId;
        if (activeTabId) {
            const activeIndex: number = availableTabs.findIndex((item: any): boolean => item.id === activeTabId);
            if (activeIndex !== -1) {
                allTabs[activeIndex].active = true;
            } else {
                availableTabs[0].active = true;
            }
        } else {
            availableTabs[0].active = true;
        }

        this.tabs = allTabs;
        this.eventEmitter.emit(this.getSelectedTab());
    }

    /**
     * Return the selected tab object
     * @returns {TabComponent}
     */
    public getSelectedTab(): TabComponent {
        if (!this.componentTabs) {
            return;
        }
        const allTabs = this.componentTabs.toArray();

        for (const tab of allTabs) {
            if (tab.active) {
                return tab;
            }
        }
    }

    /**
     * Emit the new selected tab
     */
    public tabClicked(tab: TabModel): void {
        if (!tab.disabled) {
            // Clear currently selected tabs
            this.clearActive();

            // Set as active tab
            tab.active = true;
            // emit changes
            this.eventEmitter.emit(tab);
            this.tabService.activeTabId = tab.id;
        }
    }

    /**
     * Remove the active class from all active tabs
     */
    public clearActive(): void {
        const allTabs = this.componentTabs.toArray();

        allTabs.forEach(
            (selTab: { active: boolean; }) => {
                selTab.active = false;
            }
        );
    }

    /**
     * Get all tabs that are available to view.
     * @param allTabs
     * @private
     */
    private getAvailableTabs(allTabs: any[]): any[] {
        if (this.tabPermissions) {
            const availableTabs: any[] = [];
            for (const tab of allTabs) {
                if (this.tabPermissions[tab.id]) {
                    availableTabs.push(tab);
                    tab.disabled = false;
                } else {
                    tab.disabled = true;
                }
            }

            return availableTabs;
        } else {
            return allTabs;
        }
    }
}
